import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { Messages } from 'common/components/Elements/ExpandableMessages/ExpandableMessages';
import { sideLabelStyles } from 'common/components/Elements/Form/InputDynamicLabel/InputDynamicLabel';
import InputUnsetButton from 'common/components/Elements/Form/InputUnsetButton/InputUnsetButton';
import { generateRandomString } from 'common/utilities/core/string';
import type * as I from 'core/api/coreApiClient';
import type { ValueLabelOption } from 'core/utilities/core.types';
import { inputStyles } from 'core/utilities/styles/input.styles';
import theme, { sx } from 'core/utilities/theme';
import React, { useState } from 'react';

import classes from './InputRadioGroup.style';

export interface ComponentProps {
  label?: string;
  title?: string;
  onChange: (value: string) => void;
  onReset?: () => void;
  e2eId?: string;
  options: ValueLabelOption[];
  value: string;
  horizontal?: boolean;
  small?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  errorMsg?: I.Message[];
  sideLabel?: boolean;
  modalLabel?: boolean;
  required?: boolean;
  loading?: boolean;
  helpInfo?: I.Denotation;
  inTable?: boolean;
}

// Edge case: the RadioGroup component doesn't support anything other string strings
// Always convert your values to strings and then back to what you need (number, boolean etc.)
function InputRadioGroup({
  label, value, options, onChange, horizontal, small,
  e2eId, fullWidth, errorMsg, disabled, onReset, sideLabel, modalLabel, required, loading, title, helpInfo,
  inTable,
}: ComponentProps) {
  const styles = inputStyles({
    longFormStyle: true, sideLabel, modalLabel, errorMsg, inTable,
  });
  const [id] = useState(() => generateRandomString());

  return (
    <Box mt={1} mb={0.5}>
      <FormControl
        component="fieldset"
        data-e2e-form-radio-group={e2eId}
        fullWidth={fullWidth}
        disabled={disabled}
        error={errorMsg && errorMsg.length > 0}
        sx={sx(
          { display: sideLabel ? 'flex' : 'block' },
          styles.root,
        )}
        aria-labelledby={id}
        required={required}
      >
        <Box display={sideLabel ? 'flex' : 'block'} alignItems={sideLabel ? 'flex-start' : undefined}>
          {label && (
            <FormLabel
              id={id}
              component="legend"
              title={title}
              sx={sx(
                sideLabel ? { ...sideLabelStyles, marginTop: '3px' } : undefined,
                ((small && !sideLabel) || modalLabel) ? styles.labelRoot : undefined,
                classes.label,
              )}
            >
              {label}
            </FormLabel>
          )}
          <Box display="flex" width="100%">
            <Box style={small ? { paddingLeft: '6px' } : { paddingLeft: '4px' }}>
              <RadioGroup
                aria-label={label}
                title={title}
                name={label}
                value={value}
                onChange={(e, newValue) => onChange(newValue)}
                row={horizontal}
              >
                {options.map((option) => (
                  <Box key={option.value}>
                    <FormControlLabel
                      value={option.value}
                      disabled={option.disabled}
                      control={(
                        <Radio
                          color="primary"
                          size={small ? 'small' : 'medium'}
                          sx={sx(
                            classes.radio,
                            small ? { padding: '2px' } : undefined,
                          )}
                        />
                      )}
                      label={(
                        <Typography
                          variant={small ? 'body2' : 'body1'}
                          style={option.disabled ? { color: theme.palette.text.disabled } : {}}
                        >
                          {option.label}
                        </Typography>
                      )}
                    />
                    {onReset && !horizontal && option.value === value && (
                      <InputUnsetButton onClick={() => onReset()} />
                    )}
                  </Box>
                ))}
              </RadioGroup>
              {((errorMsg && errorMsg.length > 0) || helpInfo) && (
                <FormHelperText error={errorMsg && errorMsg.length > 0} sx={{ marginLeft: 0 }} component="div">
                  <Messages messages={errorMsg} denotation={helpInfo} />
                </FormHelperText>
              )}
            </Box>
            {((onReset && horizontal) || loading) && (
              <Box flexGrow={1} display="inline-flex">
                {(onReset && horizontal) && (
                  <IconButton
                    onClick={() => onReset()}
                    size="small"
                  >
                    <ClearIcon style={{ fontSize: '16px' }} />
                    <Box mt={-0.5}><Typography variant="body2">unset</Typography></Box>
                  </IconButton>
                )}
                {loading && (
                  <Box mt={0.5}>
                    <CircularProgress
                      size={small ? 16 : 22}
                      thickness={4}
                      disableShrink
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </FormControl>
    </Box>
  );
}

export default InputRadioGroup;
